<template>
  <div id="area" class="mx-2 mt-4">
    <div v-for="(commentary, index) in preList" :key="problemId + 'pre' + index" class="prenote">
      <div
        class="note-box border-2 rounded bg-white border-gray-300 text-gray-800 dark:border-dark-400 dark:bg-dark-500 dark:text-dark-text"
      >
        <div class="bg-gray-200 dark:bg-dark-400" :class="isLightMode() ? 'top' : 'top-dark'">
          <div :class="[isLightMode() ? 'top_in_hide' : 'top_in_hide-dark']">
            <span class="material-icons mr-2 float-left"> import_contacts </span>
            {{ $t('message.topicCommentary') }}: {{ getTitle(type) }}
            <div class="dropdown relative">
              <button
                class="border py-0 pl-3 pr-2 items-center rounded focus:outline-none bg-white border-gray-500 text-gray-800 dark:bg-dark-10 dark:border-dark-10 dark:text-dark-text"
                style="width: 8rem"
              >
                <span class="mr-1" style="float: left">
                  {{ preSelect }}
                </span>
                <svg
                  class="fill-current h-4 w-4"
                  style="float: right; margin-top: 4px"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </button>
              <ul class="dropdown-menu absolute hidden shadow-xl z-10">
                <li v-for="(f, l_index) in preList" :key="l_index">
                  <button
                    class="py-1 px-3 min-w-full block whitespace-no-wrap text-left focus:outline-none notranslate border-r border-l bg-white hover:bg-gray-400 text-gray-800 dark:bg-dark-10 dark:text-dark-text dark:hover:bg-dark-200 dark:border-dark-200"
                    :class="{
                      'list-round-first border-t': l_index == 0,
                      'list-round-last border-b': l_index == preList.length - 1
                    }"
                    v-on:click="get_commentary(commentary)"
                  >
                    {{ f }}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="bottom">
          <div
            v-bind:id="class_binder(commentary)"
            class="commentary mb-3"
            :class="isLightMode() ? 'description' : 'description-dark'"
          />
          <span class="mr-1" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, Ref, watch } from 'vue'

import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useProblemDescriptionPresenter } from '@/presentation/composables/problem/useProblemDescriptionPresenter'
import { useProblemStatePresenter } from '@/presentation/composables/problem/useProblemStatePresenter'

export default defineComponent({
  components: {},
  props: {
    idNumber: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    let preSelect: Ref<string> = ref('解説表示')

    const pattern = 'pre'
    const type = 'language'
    const displayLanguage = 'ja'

    const preList = ['Python3']

    const { isLightMode } = useConfigStatePresenter()
    const { problemId } = useProblemStatePresenter()
    const problemDescriptionPresenter = useProblemDescriptionPresenter()

    const getTitle = (type: string) => {
      switch (type) {
        case 'general':
          return '一般解説'
        case 'language':
          return '言語解説'
      }
    }

    const class_binder = (target: any) => {
      return target.pattern + ':' + target.type + ':' + props.idNumber
    }

    const get_topicId = (problemId: string) => {
      return problemId.slice(0, -2)
    }

    const get_commentary = (target: any) => {
      preSelect.value = preList[0]
      const topicId = get_topicId(problemId.value)
      var url = `https://judgeapi.u-aizu.ac.jp/resources/commentaries/${displayLanguage}/${topicId}/${pattern}/${type}/${target}`
      console.log(url)
      var request = new XMLHttpRequest()
      request.open('GET', url, true)
      request.responseType = 'json'
      request.onload = function (): void {
        let html = ''
        if (request.status === 200) {
          html = this.response.html
        } else {
          html = "<p style='mt-6 mb-1'>トピック解説がありません。</p>"
        }
        if (html == null || html == undefined) {
          html = "<p style='mt-6 mb-1'>トピック解説がありません。</p>"
        }
        var div = document.createElement('div')
        div.innerHTML = html
        var target_dom = document.getElementById(class_binder(target))
        if (target_dom == null) return
        while (target_dom.firstChild) {
          target_dom.removeChild(target_dom.firstChild)
        }
        target_dom.appendChild(div)
        wait5mMathJax()
      }
      request.send()
    }

    const wait5mMathJax = () => {
      setTimeout(() => {
        // @ts-ignore
        // eslint-disable-next-line no-undef
        MathJax.typeset()
      }, 5)
    }

    //問題が切り替わった時
    watch(
      () => problemDescriptionPresenter.problemDescription.value.html,
      () => {
        wait5mMathJax()
        preSelect.value = '解説表示'
      }
    )

    onMounted(() => {
      wait5mMathJax()

      get_commentary(preList[0])
    })

    return {
      preList,
      type,
      get_commentary,
      class_binder,
      preSelect,
      getTitle,
      problemId,
      isLightMode
    }
  }
})
</script>

<style lang="postcss" scoped>
.description {
  & :deep(h1) {
    @apply text-3xl border-b-2 font-medium text-center text-gray-800;
  }

  & :deep(h2) {
    @apply text-xl mt-6 mb-1 border-b-2 text-gray-800;
  }

  & :deep(h3) {
    @apply text-lg mt-6 mb-1 border-b-2 text-gray-800;
  }

  & :deep(pre) {
    @apply p-2 my-1 rounded border bg-gray-300 whitespace-pre-wrap border-gray-400 text-gray-800;
  }

  & :deep(ul) {
    @apply list-disc pl-12 text-gray-800;
  }

  & :deep(p) {
    @apply mb-2 mt-2 text-gray-800;
  }

  & :deep(a) {
    @apply underline text-blue-500;
  }
  & :deep(table) {
    @apply my-4 border-2 border-gray-500;
  }
  & :deep(th) {
    @apply border-2 border-gray-500;
  }
  & :deep(td) {
    @apply border-2 border-gray-500;
  }
}
.description-dark {
  & :deep(h1) {
    @apply text-3xl border-b-2 font-medium text-center text-dark-text border-dark-200;
  }

  & :deep(h2) {
    @apply text-xl mt-6 mb-1 border-b-2 text-dark-text border-dark-200;
  }

  & :deep(h3) {
    @apply text-lg mt-6 mb-1 border-b-2 text-dark-text border-dark-200;
  }

  & :deep(pre) {
    @apply p-2 my-1 rounded border whitespace-pre-wrap text-dark-text border-dark-200 bg-dark-900;
  }

  & :deep(ul) {
    @apply list-disc pl-12 text-dark-text;
  }

  & :deep(p) {
    @apply mb-2 mt-2 text-dark-text;
  }

  & :deep(a) {
    @apply text-blue-500 underline;
  }
  & :deep(table) {
    @apply my-4 border-2 border-dark-100;
  }
  & :deep(th) {
    @apply border-2 border-dark-100;
  }
  & :deep(td) {
    @apply border-2 border-dark-100;
  }
}

.commentary {
  & :deep(h2) {
    @apply font-medium;
  }
  & :deep(h3) {
    @apply font-medium;
  }
}
.dropdown:hover .dropdown-menu {
  display: block;
}
.dropdown {
  float: right;
}
.note-box {
  //padding: 0.5em 1em 0.7em 1em;
  margin: 0em 0 2em 0;
}
.pnote {
  margin: 2em 0 2em 0;
}
.top {
  user-select: none;
}
.top-dark {
  user-select: none;
}
.top_in {
  padding: 0.7em 1em 0.7em 1em;
  //color: #5d627b;
}
.top_in_hide {
  padding: 0.7em 1em 0.7em 1em;
}
.top_in_hide-dark {
  padding: 0.7em 1em 0.7em 1em;
}
.bottom {
  padding: 0.5em 2em 0.7em 2em;
}
.click-effect {
  &:hover {
    @apply border-black text-black;
  }
  &:active {
    @apply bg-gray-300;
  }
}

.loading::after {
  content: '';
  position: absolute;

  width: 5em;
  height: 5em;
  top: 200px;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border: 5px white solid;
  border-top: 5px lightgray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes circ {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
.note-list:last-child {
  border-bottom: 1px solid;
}
.select {
  user-select: none;
}
.list-round-first {
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}

.list-round-last {
  border-bottom-left-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
}
</style>
